/**
 * Stretchy Post Title – frontend fit-text implementation.
 *
 * Mirrors the binary-search algorithm WordPress core uses for the
 * "Stretchy Heading" (has-fit-text) feature in the block editor, so
 * post titles with the `has-fit-text` class scale correctly on the
 * public-facing site as well.
 */

function findOptimalFontSize(
  textElement: HTMLElement,
  applyFontSize: (size: number) => void
): number {
  const alreadyHasScrollableHeight =
    textElement.scrollHeight > textElement.clientHeight;

  let minSize = 5;
  let maxSize = 2400;
  let bestSize = minSize;

  const computedStyle = window.getComputedStyle(textElement);
  let paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
  let paddingRight = parseFloat(computedStyle.paddingRight) || 0;

  const range = document.createRange();
  range.selectNodeContents(textElement);

  let referenceElement: HTMLElement = textElement;
  const parentElement = textElement.parentElement;

  if (parentElement) {
    const parentStyle = window.getComputedStyle(parentElement);
    if (parentStyle.display === 'flex') {
      referenceElement = parentElement;
      paddingLeft += parseFloat(parentStyle.paddingLeft) || 0;
      paddingRight += parseFloat(parentStyle.paddingRight) || 0;
    }
  }

  let maxClientHeight = referenceElement.clientHeight;

  while (minSize <= maxSize) {
    const midSize = Math.floor((minSize + maxSize) / 2);
    applyFontSize(midSize);

    const rect = range.getBoundingClientRect();
    const textWidth = rect.width;

    const fitsWidth =
      textElement.scrollWidth <= referenceElement.clientWidth &&
      textWidth <= referenceElement.clientWidth - paddingLeft - paddingRight;

    const fitsHeight =
      alreadyHasScrollableHeight ||
      textElement.scrollHeight <= referenceElement.clientHeight ||
      textElement.scrollHeight <= maxClientHeight;

    if (referenceElement.clientHeight > maxClientHeight) {
      maxClientHeight = referenceElement.clientHeight;
    }

    if (fitsWidth && fitsHeight) {
      bestSize = midSize;
      minSize = midSize + 1;
    } else {
      maxSize = midSize - 1;
    }
  }

  range.detach();
  return bestSize;
}

function optimizeFitText(element: HTMLElement): void {
  const computedStyle = window.getComputedStyle(element);
  const computedFontSize = parseFloat(computedStyle.fontSize) || 0;
  const computedLineHeight = computedStyle.lineHeight;

  // Preserve the original line-height proportion while scaling font-size.
  const lineHeightRatio = (() => {
    const numericLineHeight = parseFloat(computedLineHeight);

    if (Number.isNaN(numericLineHeight) || numericLineHeight <= 0) {
      return 1.2;
    }

    if (computedLineHeight.endsWith('px') && computedFontSize > 0) {
      return numericLineHeight / computedFontSize;
    }

    return numericLineHeight;
  })();

  const applyFontSize = (size: number) => {
    if (size === 0) {
      element.style.removeProperty('font-size');
      element.style.removeProperty('line-height');
    } else {
      element.style.setProperty('font-size', `${size}px`, 'important');
      element.style.setProperty(
        'line-height',
        `${Math.max(size * lineHeightRatio, 1)}px`,
        'important'
      );
    }
  };

  applyFontSize(0);
  const optimalSize = findOptimalFontSize(element, applyFontSize);
  applyFontSize(optimalSize);
}

export function initFitText(): void {
  const elements = document.querySelectorAll<HTMLElement>(
    '.wp-block-post-title.has-fit-text'
  );

  elements.forEach(el => {
    optimizeFitText(el);

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => optimizeFitText(el));
      observer.observe(el);
      if (el.parentElement) {
        observer.observe(el.parentElement);
      }
    }
  });
}
